import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import CitySelector from "./CitySelector";
import { DateTimePicker } from "@/components/ui/date-time-picker";
import { useGetAccountLocale } from "@/hooks/useGetAccountLocale";
import { useTranslation } from "@/hooks/useTranslation";
import { SubscriptionStatus, UserGender, UserStatus } from "@/generated-api";
import { useActiveSubscription, useGetUserProfile } from "@/tanstack-hooks";

export default function DisplayNameMainLocationDoB({
  form,
  gender,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form: UseFormReturn<any>;
  gender: UserGender;
}) {
  const locale = useGetAccountLocale();
  const t = useTranslation();
  const activeSubscriptionQuery = useActiveSubscription();
  const getUserProfileQuery = useGetUserProfile();

  const hasActiveSubscription =
    activeSubscriptionQuery.data?.subscription_status ===
      SubscriptionStatus.ACTIVE ||
    activeSubscriptionQuery.data?.subscription_status ===
      SubscriptionStatus.ACTIVE_CANCELED;

  const disabledNameEdit =
    gender === UserGender.MALE &&
    (getUserProfileQuery.data?.status === UserStatus.ACTIVE ||
      getUserProfileQuery.data?.status === UserStatus.ACTIVE_HIDDEN) &&
    !hasActiveSubscription;

  return (
    <>
      <FormField
        control={form.control}
        name="displayName"
        render={({ field }) => (
          <FormItem className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] items-center">
            <FormLabel>{t.USER_FORM.LABELS.DISPLAY_NAME}</FormLabel>
            <div>
              <FormControl>
                {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
                <Input
                  {...field}
                  value={field.value || ""}
                  className="text-base"
                  placeholder={
                    gender == UserGender.FEMALE
                      ? t.USER_FORM.PLACEHOLDERS.DISPLAY_NAME_FEMALE
                      : t.USER_FORM.PLACEHOLDERS.DISPLAY_NAME_MALE
                  }
                  disabled={disabledNameEdit}
                />
              </FormControl>
              {disabledNameEdit && (
                <div className="text-xs mt-1 text-zinc-600">
                  {t.USER_FORM.LABELS.UPGRADE_TO_EDIT}
                </div>
              )}
              <FormMessage className="mt-1" />
            </div>
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="mainLocation"
        render={({ field }) => (
          <FormItem className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] items-center">
            <FormLabel>{t.USER_FORM.LABELS.MAIN_LOCATION}</FormLabel>
            <div>
              <FormControl>
                <CitySelector
                  formField={field}
                  placeholder={t.USER_FORM.PLACEHOLDERS.CITY_INPUT}
                />
              </FormControl>
              <FormMessage className="mt-1" />
            </div>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="dob"
        render={({ field }) => (
          <FormItem className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] items-center">
            <FormLabel htmlFor="dob">
              {t.USER_FORM.LABELS.DATE_OF_BIRTH}
            </FormLabel>
            <div>
              <FormControl>
                <DateTimePicker
                  locale={locale}
                  yearRange={80}
                  displayFormat={{ hour24: "MMMM dd, yyyy" }}
                  granularity="day"
                  value={field.value}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage className="mt-1" />
            </div>
          </FormItem>
        )}
      />
    </>
  );
}
